import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { BlogPost } from "@/types/Blog";
import { ArrowLeft, Save, Eye, Copy } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { ImageUpload } from "./ImageUpload";
import { AIContentGenerator } from "./AIContentGenerator";

interface BlogPostEditorProps {
  post?: BlogPost | null;
  onSave: (postData: Partial<BlogPost>) => void;
  onCancel: () => void;
}

const categories = ["Productivity", "Business", "Finance", "Technology", "Design", "Marketing"];

const defaultAuthor = {
  id: "1",
  name: "Alex Morgan",
  avatar: "/images/blog/author-alex.jpg",
  role: "Productivity Coach",
  bio: "Alex has been freelancing for over 8 years and coaches other freelancers on productivity and business growth."
};

export function BlogPostEditor({ post, onSave, onCancel }: BlogPostEditorProps) {
  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    excerpt: "",
    content: "",
    category: "Productivity",
    tags: "",
    readingTime: 5,
    featuredImage: "",
    author: defaultAuthor
  });

  const [showCodeOutput, setShowCodeOutput] = useState(false);

  useEffect(() => {
    if (post) {
      setFormData({
        title: post.title,
        slug: post.slug,
        excerpt: post.excerpt,
        content: post.content,
        category: post.category,
        tags: post.tags.join(", "),
        readingTime: post.readingTime,
        featuredImage: post.featuredImage || "",
        author: post.author
      });
    }
  }, [post]);

  const handleInputChange = (field: string, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Auto-generate slug from title
    if (field === "title") {
      const slug = value.toString()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");
      setFormData(prev => ({ ...prev, slug }));
    }
  };

  const handleAIContentGenerated = (content: string, type: 'title' | 'excerpt' | 'content' | 'tags') => {
    switch (type) {
      case 'title':
        handleInputChange('title', content);
        break;
      case 'excerpt':
        handleInputChange('excerpt', content);
        break;
      case 'content':
        handleInputChange('content', content);
        break;
      case 'tags':
        handleInputChange('tags', content);
        break;
    }
  };

  const generatePostCode = () => {
    const newPost = {
      id: post?.id || `"${Date.now()}"`,
      title: `"${formData.title}"`,
      slug: `"${formData.slug}"`,
      excerpt: `"${formData.excerpt}"`,
      content: `\`${formData.content.replace(/`/g, '\\`')}\``,
      publishedAt: `"${new Date().toISOString()}"`,
      updatedAt: `"${new Date().toISOString()}"`,
      category: `"${formData.category}"`,
      tags: `[${formData.tags.split(",").map(tag => `"${tag.trim()}"`).join(", ")}]`,
      readingTime: formData.readingTime,
      coverImage: `"/images/blog/${formData.slug}.jpg"`,
      featuredImage: formData.featuredImage ? `"${formData.featuredImage}"` : 'undefined',
      author: `{
        id: "${formData.author.id}",
        name: "${formData.author.name}",
        avatar: "${formData.author.avatar}",
        role: "${formData.author.role}",
        bio: "${formData.author.bio}"
      }`
    };

    return `{
  id: ${newPost.id},
  title: ${newPost.title},
  slug: ${newPost.slug},
  excerpt: ${newPost.excerpt},
  content: ${newPost.content},
  publishedAt: ${newPost.publishedAt},
  updatedAt: ${newPost.updatedAt},
  category: ${newPost.category},
  tags: ${newPost.tags},
  readingTime: ${newPost.readingTime},
  coverImage: ${newPost.coverImage},${formData.featuredImage ? `
  featuredImage: ${newPost.featuredImage},` : ''}
  author: ${newPost.author}
}`;
  };

  const handleSave = () => {
    if (!formData.title || !formData.excerpt || !formData.content) {
      toast({
        title: "Missing required fields",
        description: "Please fill in title, excerpt, and content",
        variant: "destructive",
      });
      return;
    }

    const postData = {
      ...formData,
      tags: formData.tags.split(",").map(tag => tag.trim()).filter(Boolean),
      publishedAt: post?.publishedAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    onSave(postData);
  };

  const copyCodeToClipboard = () => {
    const code = generatePostCode();
    navigator.clipboard.writeText(code).then(() => {
      toast({
        title: "Code copied!",
        description: "Post code has been copied to clipboard. Add it to src/data/blogPosts.ts",
        duration: 5000,
      });
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onCancel} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          Back to Posts
        </Button>
        
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowCodeOutput(!showCodeOutput)} className="gap-2">
            <Eye className="h-4 w-4" />
            {showCodeOutput ? "Hide" : "Show"} Code
          </Button>
          <Button onClick={handleSave} className="gap-2">
            <Save className="h-4 w-4" />
            Save Post
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Featured Image</CardTitle>
            </CardHeader>
            <CardContent>
              <ImageUpload
                value={formData.featuredImage}
                onChange={(url) => handleInputChange("featuredImage", url)}
                onRemove={() => handleInputChange("featuredImage", "")}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => handleInputChange("title", e.target.value)}
                  placeholder="Enter post title"
                />
              </div>

              <div>
                <Label htmlFor="slug">URL Slug</Label>
                <Input
                  id="slug"
                  value={formData.slug}
                  onChange={(e) => handleInputChange("slug", e.target.value)}
                  placeholder="auto-generated-from-title"
                />
              </div>

              <div>
                <Label htmlFor="excerpt">Excerpt *</Label>
                <Textarea
                  id="excerpt"
                  value={formData.excerpt}
                  onChange={(e) => handleInputChange("excerpt", e.target.value)}
                  placeholder="Brief description of the post"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="category">Category</Label>
                  <Select value={formData.category} onValueChange={(value) => handleInputChange("category", value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(category => (
                        <SelectItem key={category} value={category}>
                          {category}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="readingTime">Reading Time (minutes)</Label>
                  <Input
                    id="readingTime"
                    type="number"
                    value={formData.readingTime}
                    onChange={(e) => handleInputChange("readingTime", parseInt(e.target.value) || 5)}
                    min="1"
                    max="60"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="tags">Tags (comma-separated)</Label>
                <Input
                  id="tags"
                  value={formData.tags}
                  onChange={(e) => handleInputChange("tags", e.target.value)}
                  placeholder="productivity, freelancing, time-management"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Content *</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={formData.content}
                onChange={(e) => handleInputChange("content", e.target.value)}
                placeholder="Write your blog post content in HTML format..."
                rows={20}
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground mt-2">
                Use HTML tags for formatting: &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;strong&gt;, etc.
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <AIContentGenerator 
            onContentGenerated={handleAIContentGenerated}
            currentContent={{
              title: formData.title,
              excerpt: formData.excerpt,
              content: formData.content,
              category: formData.category
            }}
          />

          {showCodeOutput && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  Generated Code
                  <Button variant="outline" size="sm" onClick={copyCodeToClipboard} className="gap-2">
                    <Copy className="h-3 w-3" />
                    Copy
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="text-xs bg-muted p-4 rounded-md overflow-auto max-h-96">
                  <code>{generatePostCode()}</code>
                </pre>
                <Separator className="my-4" />
                <div className="text-sm text-muted-foreground space-y-2">
                  <p><strong>Instructions:</strong></p>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Copy the code above</li>
                    <li>Open <code>src/data/blogPosts.ts</code></li>
                    <li>Add the code to the <code>blogPosts</code> array</li>
                    <li>Deploy your changes</li>
                  </ol>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
